import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { etiquetaEntidad, explicarRegistro } from "@/lib/describeDetalle";
import type { RegistroAuditoria } from "@/types";

export interface RegistroAuditoriaRow extends Omit<RegistroAuditoria, "usuario"> {
  usuario: { id: number; nombre: string; apellidos: string | null; email: string } | null;
  club: { id: number; nombre: string } | null;
}

const fechaFormatter = new Intl.DateTimeFormat("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const horaFormatter = new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

function Fila({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-3 sm:gap-3">
      <dt className="text-xs font-medium text-ink-400 sm:text-sm">{etiqueta}</dt>
      <dd className="text-sm break-words text-ink-800 sm:col-span-2">{valor}</dd>
    </div>
  );
}

// Ventana con la explicación de un registro de Logs: qué se hizo, quién, cuándo y qué datos. Todo
// en texto normal (sin JSON) y con ajuste de línea, así que nunca hace falta scroll horizontal.
export function AuditoriaDetalleModal({ registro, onOpenChange }: { registro: RegistroAuditoriaRow; onOpenChange: (open: boolean) => void }) {
  const fecha = new Date(registro.createdAt);
  const e = explicarRegistro({
    accion: registro.accion,
    entidad: registro.entidad,
    entidadId: registro.entidadId,
    detalle: registro.detalle,
    usuario: registro.usuario,
    club: registro.club,
  });
  const quien = registro.usuario ? `${registro.usuario.nombre} ${registro.usuario.apellidos ?? ""}`.trim() : "Sistema";

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={e.titulo}
      description={`${fechaFormatter.format(fecha)} a las ${horaFormatter.format(fecha)}`}
    >
      <div className="flex flex-col gap-5">
        <div className="rounded-xl bg-brand-50/60 p-4">
          <p className="text-xs font-semibold tracking-wide text-brand-700 uppercase">Qué se hizo</p>
          <p className="mt-1.5 text-sm leading-relaxed break-words text-ink-800">{e.resumen}</p>
        </div>

        <dl className="flex flex-col gap-2.5">
          <Fila etiqueta="Quién" valor={quien} />
          {registro.usuario && <Fila etiqueta="Email" valor={registro.usuario.email} />}
          {registro.club && <Fila etiqueta="Club" valor={registro.club.nombre} />}
          {registro.entidad && (
            <Fila etiqueta="Sobre" valor={`${etiquetaEntidad(registro.entidad)}${registro.entidadId != null ? ` nº ${registro.entidadId}` : ""}`} />
          )}
        </dl>

        {e.datos.length > 0 && (
          <div className="flex flex-col gap-2.5 border-t border-ink-100 pt-4">
            <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Datos registrados</p>
            <dl className="flex flex-col gap-2.5">
              {e.datos.map((x) => (
                <Fila key={x.etiqueta} etiqueta={x.etiqueta} valor={x.valor} />
              ))}
            </dl>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
        </div>
      </div>
    </Modal>
  );
}
